import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Mic2, Brain, BookOpen, LineChart, Target, Settings as SettingsIcon,
  ChevronLeft, ChevronRight, LogOut
} from "lucide-react";
import { supabase } from "../../lib/supabase";

const ITEMS = [
  { to: "/dashboard", label: "Today", icon: Home, end: true },
  { to: "/dashboard/session", label: "Session", icon: Mic2 },
  { to: "/dashboard/memory", label: "Memory", icon: Brain },
  { to: "/dashboard/practice", label: "Practice", icon: BookOpen },
  { to: "/dashboard/progress", label: "Progress", icon: LineChart },
  { to: "/dashboard/goals", label: "Goals", icon: Target },
];

const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const [profile, setProfile] = useState({ display_name: "", cefr_level: "A1", target_language: "" });

  // Close mobile drawer on route change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onMobileClose?.(); }, [location.pathname]);

  // Load real user data
  useEffect(() => {
    async function loadProfile() {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData?.user?.id;
      if (!uid) return;
      const { data } = await supabase
        .from("users")
        .select("display_name, cefr_level, target_language")
        .eq("id", uid)
        .maybeSingle();
      if (data) setProfile(data);
    }
    loadProfile();
    // Re-load when auth state changes (after settings save)
    const { data: sub } = supabase.auth.onAuthStateChange(() => loadProfile());
    
    // Listen for custom profile update events (from Settings)
    const handleProfileUpdate = () => loadProfile();
    window.addEventListener("aura_profile_updated", handleProfileUpdate);

    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener("aura_profile_updated", handleProfileUpdate);
    };
  }, []);

  const initial = profile.display_name?.[0]?.toUpperCase() || "?";
  const name = profile.display_name || "You";
  const lang = profile.target_language ? profile.target_language.slice(0, 2).toUpperCase() : "–";
  const cefr = profile.cefr_level || "A1";

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <>
      {/* Backdrop on mobile */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-40 bg-base/60 backdrop-blur-sm"
            onClick={onMobileClose}
            data-testid="sidebar-backdrop"
          />
        )}
      </AnimatePresence>

      <aside
        data-testid="dashboard-sidebar"
        className={`fixed lg:sticky top-0 left-0 h-screen z-50 lg:z-30 flex flex-col border-r border-panel/6 transition-[width,transform] duration-300
          ${collapsed ? "lg:w-[76px]" : "lg:w-[244px]"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          w-[244px] bg-elev/85 backdrop-blur-xl`}
      >
        {/* Logo */}
        <div className="px-5 h-16 flex items-center justify-between border-b border-panel/6">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="sidebar-logo">
            <span className="relative inline-block w-2.5 h-2.5 rounded-full bg-aura-cyan shadow-[0_0_12px_rgb(var(--accent-cyan-rgb)/0.9)] shrink-0">
              <span className="absolute inset-0 rounded-full animate-ping bg-aura-cyan/60" />
            </span>
            {!collapsed && (
              <span className="font-display text-[20px] tracking-[-0.02em] text-ink font-light truncate">
                AURA<span className="text-aura-cyan">.</span>
              </span>
            )}
          </Link>
          <button
            onClick={onToggle}
            className="hidden lg:grid w-7 h-7 place-items-center rounded-md hover:bg-panel/5 text-whisper-muted"
            aria-label="Collapse sidebar"
            data-testid="sidebar-collapse"
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                  isActive
                    ? "bg-aura-cyan/[0.08] text-ink"
                    : "text-whisper hover:text-ink hover:bg-panel/[0.04]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="sidebar-indicator"
                      className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-aura-cyan shadow-[0_0_10px_rgb(var(--accent-cyan-rgb)/0.6)]"
                    />
                  )}
                  <item.icon size={17} strokeWidth={1.5} className={isActive ? "text-aura-cyan" : ""} />
                  {!collapsed && <span className="font-mono text-[12px] tracking-[0.16em] uppercase">{item.label}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: start session CTA + profile */}
        <div className="border-t border-panel/6 p-3 space-y-2">
          <Link
            to="/dashboard/session"
            className="btn-primary w-full justify-center"
            data-testid="sidebar-start-session"
          >
            {collapsed ? <Mic2 size={14} /> : <>start session <Mic2 size={14} /></>}
          </Link>
          <NavLink
            to="/dashboard/settings"
            data-testid="sidebar-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                isActive ? "bg-panel/[0.04] text-ink" : "text-whisper hover:text-ink hover:bg-panel/[0.04]"
              }`
            }
          >
            <SettingsIcon size={17} strokeWidth={1.5} />
            {!collapsed && <span className="font-mono text-[12px] tracking-[0.16em] uppercase">Settings</span>}
          </NavLink>

          {/* Real user profile */}
          <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${collapsed ? "justify-center" : ""}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-aura-cyan/30 to-aura-violet/30 border border-panel/10 grid place-items-center font-display text-ink text-[14px] shrink-0">
              {initial}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-ink truncate">{name}</div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted">{cefr} · {lang}</div>
              </div>
            )}
          </div>

          {!collapsed && (
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-whisper-muted hover:text-ink transition-colors w-full"
              data-testid="sidebar-sign-out"
            >
              <LogOut size={14} />
              <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase">Sign out</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
